// Shared presence utilities to keep presence indicators consistent across the app

export const ONLINE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

// Map MUI color tokens to hex used for small avatar dots
export function colorTokenToHex(token) {
  switch (token) {
    case 'success.main':
      return '#22c55e';
    case 'warning.main':
      return '#f59e0b';
    case 'error.main':
      return '#ef4444';
    case 'grey.400':
    default:
      return '#9ca3af';
  }
}

// Compute presence display state using the same logic as StaffDirectoryPage.computePresence
// Accepts a presence object (optionally containing status, statusMessage, lastActiveAt/lastSeenAt)
export function computePresenceFromPresence(presence, thresholdMs = ONLINE_THRESHOLD_MS) {
  const p = presence || {};
  const statusRaw = p.status || '';
  const status = String(statusRaw).toLowerCase();
  const lastActiveAtStr = p.lastActiveAt || p.lastSeenAt || null;
  const lastActiveAt = lastActiveAtStr ? new Date(lastActiveAtStr) : null;

  let activeRecently = false;
  if (lastActiveAt && !isNaN(lastActiveAt.getTime())) {
    activeRecently = (Date.now() - lastActiveAt.getTime()) <= thresholdMs;
  }

  // If not recently active or never logged in, treat as offline (gray)
  if (!activeRecently) {
    return { isActive: false, colorToken: 'grey.400', label: 'Offline' };
  }

  // Recently active: map status to color
  if (status === 'busy' || status === 'dnd' || status === 'do_not_disturb') {
    return { isActive: true, colorToken: 'error.main', label: p.statusMessage || 'Busy' };
  }

  if (status === 'away' || status === 'idle' || status === 'lunch') {
    const label = p.statusMessage || (status === 'lunch' ? 'Lunch' : 'Away');
    return { isActive: true, colorToken: 'warning.main', label };
  }

  // Default to available/online (including 'custom' status)
  return { isActive: true, colorToken: 'success.main', label: p.statusMessage || 'Online' };
}

export function getPresenceBadgeColorHex(presence) {
  const state = computePresenceFromPresence(presence);
  return colorTokenToHex(state.colorToken);
}

export function buildPresenceTooltipFromPresence(presence) {
  const state = computePresenceFromPresence(presence);
  if (!state.isActive) {
    const last = (presence && (presence.lastActiveAt || presence.lastSeenAt)) || null;
    return last ? `Offline — last seen ${last}` : 'Offline';
  }
  return state.label === 'Online' ? 'Online' : `Online (${state.label})`;
}
